import { useState } from 'react'
import { api } from '../lib/api.js'

const PROVIDERS = [
  { id: 'anthropic', label: 'Claude (Anthropic)', keyField: 'ai_api_key', url: 'https://console.anthropic.com/settings/keys', recommended: true },
  { id: 'openai', label: 'OpenAI (GPT)', keyField: 'openai_api_key', url: 'https://platform.openai.com/api-keys' },
  { id: 'xai', label: 'xAI (Grok)', keyField: 'xai_api_key', url: 'https://console.x.ai/' },
  { id: 'ollama', label: 'Ollama (lokal, gratis)', keyField: null, url: null },
]

const EMAIL_PROVIDERS = {
  'gmail.com': { imap: 'imap.gmail.com', smtp: 'smtp.gmail.com' },
  'googlemail.com': { imap: 'imap.gmail.com', smtp: 'smtp.gmail.com' },
  'outlook.com': { imap: 'outlook.office365.com', smtp: 'smtp.office365.com' },
  'hotmail.com': { imap: 'outlook.office365.com', smtp: 'smtp.office365.com' },
  'live.com': { imap: 'outlook.office365.com', smtp: 'smtp.office365.com' },
  'yahoo.com': { imap: 'imap.mail.yahoo.com', smtp: 'smtp.mail.yahoo.com' },
  'icloud.com': { imap: 'imap.mail.me.com', smtp: 'smtp.mail.me.com' },
  'me.com': { imap: 'imap.mail.me.com', smtp: 'smtp.mail.me.com' },
  'bluewin.ch': { imap: 'imaps.bluewin.ch', smtp: 'smtps.bluewin.ch' },
}

export default function OnboardingWizard({ onComplete, initialStep }) {
  const [step, setStep] = useState(initialStep || 0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)

  // Step 0: Büro-Profil
  const [companyName, setCompanyName] = useState('')
  const [logoData, setLogoData] = useState(null)

  // Step 1: KI-Provider
  const [provider, setProvider] = useState('anthropic')
  const [apiKey, setApiKey] = useState('')
  const [ollamaModels, setOllamaModels] = useState(null)
  const [selectedModel, setSelectedModel] = useState('')

  // Step 2: Email
  const [emailAddr, setEmailAddr] = useState('')
  const [emailPassword, setEmailPassword] = useState('')
  const [imapHost, setImapHost] = useState('')
  const [smtpHost, setSmtpHost] = useState('')

  // Step 3: Erstes Projekt
  const [projectName, setProjectName] = useState('')
  const [projectNumber, setProjectNumber] = useState('')

  const totalSteps = 4

  function autoDetectEmail(email) {
    const domain = email.split('@')[1]?.toLowerCase()
    if (domain && EMAIL_PROVIDERS[domain]) {
      const p = EMAIL_PROVIDERS[domain]
      setImapHost(p.imap)
      setSmtpHost(p.smtp)
    }
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => setLogoData(reader.result)
    reader.readAsDataURL(file)
  }

  async function saveStep0() {
    if (!companyName.trim()) { setError('Bitte Büro-Name eingeben'); return }
    setSaving(true); setError('')
    try {
      await api.saveSettings({ company_name: companyName.trim() })
      if (logoData) {
        await api.uploadCompanyLogo(logoData)
      }
      setStep(1)
    } catch (err) {
      setError(err.message || 'Speichern fehlgeschlagen')
    } finally { setSaving(false) }
  }

  async function testProvider() {
    setTesting(true); setTestResult(null); setError('')
    try {
      if (provider === 'ollama') {
        const result = await api.ollamaModels()
        if (result.models?.length > 0) {
          setOllamaModels(result.models)
          setSelectedModel(result.models[0].name)
          setTestResult({ ok: true, message: `${result.models.length} Modelle gefunden` })
        } else {
          setTestResult({ ok: false, message: 'Keine Modelle installiert. Bitte zuerst "ollama pull llama3.2" ausführen.' })
        }
      } else {
        const keyField = PROVIDERS.find(p => p.id === provider)?.keyField
        if (!apiKey.trim()) { setError('Bitte API-Key eingeben'); setTesting(false); return }
        // Speichern und schauen ob es klappt
        await api.saveSettings({ ai_provider: provider, [keyField]: apiKey.trim() })
        setTestResult({ ok: true, message: 'API-Key gespeichert und Provider konfiguriert' })
      }
    } catch (err) {
      setTestResult({ ok: false, message: err.message || 'Verbindung fehlgeschlagen' })
    } finally { setTesting(false) }
  }

  async function saveStep1() {
    setError('')
    if (provider === 'ollama') {
      if (!ollamaModels || ollamaModels.length === 0) {
        setError('Bitte zuerst Verbindung testen')
        return
      }
      setSaving(true)
      try {
        await api.saveSettings({ ai_provider: 'ollama', ai_model: selectedModel || ollamaModels[0].name })
        setStep(2)
      } catch (err) {
        setError(err.message)
      } finally { setSaving(false) }
    } else {
      const keyField = PROVIDERS.find(p => p.id === provider)?.keyField
      if (!apiKey.trim()) { setError('Bitte API-Key eingeben'); return }
      setSaving(true)
      try {
        await api.saveSettings({ ai_provider: provider, [keyField]: apiKey.trim() })
        setStep(2)
      } catch (err) {
        setError(err.message)
      } finally { setSaving(false) }
    }
  }

  async function saveStep2() {
    if (!emailAddr.trim()) { skipStep2(); return }
    if (!emailPassword) { setError('Bitte Passwort eingeben'); return }
    if (!imapHost.trim()) { setError('Bitte IMAP-Host eingeben'); return }
    setSaving(true); setError('')
    try {
      await api.saveEmailSettings({
        email: emailAddr.trim(),
        password: emailPassword,
        imap_host: imapHost.trim(),
        smtp_host: smtpHost.trim() || undefined,
        imap_port: 993,
      })
      setStep(3)
    } catch (err) {
      setError(err.message || 'Email-Konfiguration fehlgeschlagen')
    } finally { setSaving(false) }
  }

  // KI-Provider ueberspringen: Urs laeuft ueber das Claude-Abo (claude-code, kein API-Key)
  async function skipStep1() {
    setSaving(true); setError('')
    try {
      await api.saveSettings({ ai_provider: 'claude', chat_mode: 'claude-code' })
      setStep(2)
    } catch (err) {
      setError(err.message)
    } finally { setSaving(false) }
  }

  function skipStep2() {
    setStep(3)
  }

  async function saveStep3() {
    if (!projectName.trim()) { finish(); return }
    setSaving(true); setError('')
    try {
      await api.createProject({
        name: projectName.trim(),
        project_number: projectNumber.trim() || null,
      })
      finish()
    } catch (err) {
      setError(err.message || 'Projekt konnte nicht erstellt werden')
      setSaving(false)
    }
  }

  function skipStep3() {
    finish()
  }

  function finish() {
    setSaving(false)
    api.setupComplete().catch(() => {})
    onComplete?.()
  }

  const stepLabels = ['Büro', 'KI-Provider', 'Email', 'Projekt']

  return (
    <div className="modal-overlay" style={{ zIndex: 1000 }}>
      <div className="modal-card" style={{ maxWidth: 520, width: '94%', maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        {/* Progress */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
          {stepLabels.map((label, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                height: 3, borderRadius: 2, marginBottom: 4,
                background: i <= step ? 'var(--brand)' : 'var(--border)',
                transition: 'background 0.3s',
              }} />
              <span style={{ fontSize: 11, color: i <= step ? 'var(--text-1)' : 'var(--text-3)' }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Step 0: Willkommen + Büro */}
        {step === 0 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 8 }}>
                <rect x="3" y="11" width="18" height="10" rx="2" />
                <circle cx="12" cy="5" r="2" />
                <path d="M12 7v4" />
                <circle cx="8.5" cy="16" r="1.5" fill="var(--brand)" stroke="none" />
                <circle cx="15.5" cy="16" r="1.5" fill="var(--brand)" stroke="none" />
              </svg>
              <h2 style={{ margin: '0 0 4px' }}>Willkommen bei Urs</h2>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>Dein KI-Assistent fürs Büro. Lass uns in wenigen Schritten alles einrichten.</p>
            </div>

            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Büro-Name *</label>
            <input className="input" value={companyName} onChange={e => setCompanyName(e.target.value)}
              placeholder="z.B. Muster Architekten AG" autoFocus />

            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginTop: 12, marginBottom: 4 }}>Logo (optional)</label>
            <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ fontSize: 13 }} />
            {logoData && <img src={logoData} alt="Logo" style={{ height: 40, marginTop: 8, objectFit: 'contain' }} />}

            {error && <p style={{ color: 'var(--color-danger)', fontSize: 13, marginTop: 8 }}>{error}</p>}
            <div className="modal-actions" style={{ marginTop: 20 }}>
              <button className="btn btn-primary" onClick={saveStep0} disabled={saving}>
                {saving ? 'Speichere...' : 'Weiter'}
              </button>
            </div>
          </div>
        )}

        {/* Step 1: KI-Provider */}
        {step === 1 && (
          <div>
            <h3 style={{ marginBottom: 4 }}>KI-Provider einrichten</h3>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginBottom: 16 }}>Urs antwortet standardmässig über dein Claude-Abo – ohne API-Key. Einen API-Key brauchst du nur für nutzungsbasierte Abrechnung oder wenn du Urs an andere weitergibst.</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              {PROVIDERS.map(p => (
                <label key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                  borderRadius: 6, cursor: 'pointer',
                  border: provider === p.id ? '1.5px solid var(--color-primary)' : '1px solid var(--color-border)',
                  background: provider === p.id ? 'var(--color-primary-bg, rgba(255,85,0,0.06))' : 'transparent',
                }}>
                  <input type="radio" name="provider" value={p.id} checked={provider === p.id}
                    onChange={() => { setProvider(p.id); setTestResult(null); setError(''); setOllamaModels(null) }} />
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{p.label}</span>
                  {p.recommended && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'var(--color-primary)', color: '#fff' }}>empfohlen</span>}
                </label>
              ))}
            </div>

            {provider !== 'ollama' && (
              <>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>API-Key *</label>
                <input className="input" type="password" value={apiKey} onChange={e => setApiKey(e.target.value)}
                  placeholder={provider === 'anthropic' ? 'sk-ant-...' : provider === 'openai' ? 'sk-...' : 'xai-...'} />
                {PROVIDERS.find(p => p.id === provider)?.url && (
                  <a href={PROVIDERS.find(p => p.id === provider).url} target="_blank" rel="noreferrer"
                    style={{ fontSize: 12, color: 'var(--color-primary)', display: 'inline-block', marginTop: 4 }}>
                    API-Key hier erstellen &rarr;
                  </a>
                )}
              </>
            )}

            {provider === 'ollama' && (
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', padding: '8px 0' }}>
                Ollama muss lokal installiert und gestartet sein (<code>ollama serve</code>).
                {ollamaModels && ollamaModels.length > 0 && (
                  <select className="input" value={selectedModel} onChange={e => setSelectedModel(e.target.value)} style={{ marginTop: 8 }}>
                    {ollamaModels.map(m => <option key={m.name} value={m.name}>{m.name}</option>)}
                  </select>
                )}
              </p>
            )}

            <button className="btn btn-secondary btn-sm" onClick={testProvider} disabled={testing} style={{ marginTop: 8 }}>
              {testing ? 'Teste...' : 'Verbindung testen'}
            </button>

            {testResult && (
              <p style={{ fontSize: 13, marginTop: 6, color: testResult.ok ? 'var(--color-success, #22c55e)' : 'var(--color-danger, #ef4444)' }}>
                {testResult.ok ? '\u2713' : '\u2717'} {testResult.message}
              </p>
            )}

            {error && <p style={{ color: 'var(--color-danger)', fontSize: 13, marginTop: 8 }}>{error}</p>}
            <div className="modal-actions" style={{ marginTop: 20 }}>
              <button className="btn btn-secondary" onClick={() => setStep(0)}>Zurück</button>
              <button className="btn btn-secondary" onClick={skipStep1} disabled={saving}>Ich nutze das Claude-Abo</button>
              <button className="btn btn-primary" onClick={saveStep1} disabled={saving}>
                {saving ? 'Speichere...' : 'Weiter'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Email */}
        {step === 2 && (
          <div>
            <h3 style={{ marginBottom: 4 }}>Email einrichten</h3>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginBottom: 16 }}>
              Verbinde dein Postfach, damit Urs Emails lesen und beantworten kann.
            </p>

            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Email-Adresse</label>
            <input className="input" type="email" value={emailAddr}
              onChange={e => { setEmailAddr(e.target.value); autoDetectEmail(e.target.value) }}
              placeholder="name@büro.ch" />

            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginTop: 12, marginBottom: 4 }}>Passwort</label>
            <input className="input" type="password" value={emailPassword}
              onChange={e => setEmailPassword(e.target.value)} placeholder="Email-Passwort oder App-Passwort" />

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>IMAP-Host</label>
                <input className="input" value={imapHost} onChange={e => setImapHost(e.target.value)} placeholder="imap.example.com" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>SMTP-Host</label>
                <input className="input" value={smtpHost} onChange={e => setSmtpHost(e.target.value)} placeholder="smtp.example.com" />
              </div>
            </div>

            {error && <p style={{ color: 'var(--color-danger)', fontSize: 13, marginTop: 8 }}>{error}</p>}
            <div className="modal-actions" style={{ marginTop: 20 }}>
              <button className="btn btn-secondary" onClick={() => setStep(1)}>Zurück</button>
              <button className="btn btn-secondary" onClick={skipStep2}>Später einrichten</button>
              <button className="btn btn-primary" onClick={saveStep2} disabled={saving}>
                {saving ? 'Speichere...' : 'Weiter'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Erstes Projekt */}
        {step === 3 && (
          <div>
            <h3 style={{ marginBottom: 4 }}>Erstes Projekt anlegen</h3>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginBottom: 16 }}>
              Projekte helfen dir, Emails, Aufgaben und Dokumente zu organisieren.
            </p>

            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Projektname</label>
            <input className="input" value={projectName} onChange={e => setProjectName(e.target.value)}
              placeholder="z.B. Neubau Wohnhaus Musterstrasse" autoFocus />

            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginTop: 12, marginBottom: 4 }}>Projektnummer (optional)</label>
            <input className="input" value={projectNumber} onChange={e => setProjectNumber(e.target.value)}
              placeholder="z.B. 25-012" />

            {error && <p style={{ color: 'var(--color-danger)', fontSize: 13, marginTop: 8 }}>{error}</p>}
            <div className="modal-actions" style={{ marginTop: 20 }}>
              <button className="btn btn-secondary" onClick={() => setStep(2)}>Zurück</button>
              <button className="btn btn-secondary" onClick={skipStep3}>Später einrichten</button>
              <button className="btn btn-primary" onClick={saveStep3} disabled={saving}>
                {saving ? 'Erstelle...' : 'Abschliessen'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
